import { IMGS_PATH } from 'constants/image';
import clsx from 'clsx';
import ExternalFunnel from 'components/Shared/ExternalFunnel/ExternalFunnel';
import Breadcrumbs from 'components/ui/Breadcrumbs';
import Icon from 'components/ui/Icon';
import Image from 'components/ui/Image';
import styles from 'components/ui/Image.module.scss';
import { hideConversionBarUntilScroll } from 'utils/hideConversionBarUntilScroll';
import type { LocationPage } from 'types/PageData';

type Props = {
  entry: LocationPage;
};

const Hero = ({ entry }: Props): JSX.Element => {
  const { heroImage, speciality, treatment } = entry || {};

  const breadcrumbs = [
    {
      link: speciality.slug,
      title: speciality.specialityName || speciality.title,
    },
    {
      link: `${speciality.slug}/${treatment.slug}`,
      title: treatment.treatmentName || treatment.title,
    },
    {
      link: entry.slug,
      title: entry.title as string,
    },
  ];

  // eslint-disable-next-line
  // @ts-ignore
  if (entry.funnel && entry.funnel.fields.fullWidth) {
    // -- we need to force a non fullWidth funnel
    // eslint-disable-next-line
    // @ts-ignore
    delete entry.funnel.fields.fullWidth;
  }

  const renderImage = (isMobile?: boolean): JSX.Element => {
    const additionalProps = isMobile
      ? { fill: true }
      : {
          height: 570,
          width: 1040,
        };

    return (
      <Image
        className={clsx(
          'float-right rounded rounded-bl-2xl object-cover',
          heroImage ? 'xl:w-10/12' : 'w-10/12',
          styles.locationPageHero,
        )}
        src={heroImage?.url || `${IMGS_PATH.PUBLIC}/location-page-default.jpg`}
        alt={heroImage?.description || 'Location Page Default Hero'}
        contentful
        priority
        {...additionalProps}
      />
    );
  };

  return (
    <>
      <Breadcrumbs crumbs={breadcrumbs} />
      <div className="max-w-screen-xl px-5 lg:px-4 mx-auto">
        <div className="lg:flex items-end justify-between mb-5 lg:mb-10">
          <h1 className="leading-10 mt-6 mb-5 lg:my-0 pt-0">
            <span className="text-1-75r lg:text-1_2xl">
              {treatment.treatmentName} in
            </span>
            <br />
            <span className="text-2-75r lg:text-3-375r font-netto">
              {entry.title as string}
            </span>
          </h1>
          <div className="md:flex lg:justify-end flex-wrap text-navy leading-8">
            {entry.keyFactFlight && (
              <div className="flex items-center font-netto text-lg mr-4">
                <Icon type="Airplane" className="mr-3" color="#155263" />
                {entry.keyFactFlight}
              </div>
            )}
            {entry.keyFactCurrency && (
              <div className="flex items-center font-netto text-lg mr-4">
                <Icon type="Currency" className="mr-3" color="#155263" />
                {entry.keyFactCurrency}
              </div>
            )}
            {entry.keyFactLanguage && (
              <div className="flex items-center font-netto text-lg">
                <Icon type="Languages" className="mr-3" color="#155263" />
                {entry.keyFactLanguage}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="max-w-screen-xl lg:px-4 mx-auto">
        <div className="flex flex-col">
          {entry.funnel && (
            <div className="lg:absolute z-10 lg:w-88 mt-4 order-2 lg:order-1 px-5 lg:px-0">
              <ExternalFunnel
                funnelId={entry.funnel.sys.id}
                containerClasses="bg-white lg:ml-0"
                roundedCorner="rounded-bl-2xl"
                onInteraction={hideConversionBarUntilScroll}
                useShadow
              />
            </div>
          )}

          <div
            className={clsx(
              'text-right mb-6',
              heroImage && 'order-1 lg:order-2',
            )}
          >
            <div
              className={clsx(
                'hidden',
                heroImage
                  ? 'lg:flex flex-col items-end'
                  : 'overflow-hidden lg:block',
              )}
            >
              {renderImage()}
            </div>
            <div className="overflow-hidden relative lg:hidden w-full aspect-video">
              {renderImage(true)}
            </div>
            {heroImage && (
              <p className="px-3 lg:px-0 text-sm pt-1">
                {heroImage.description}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Hero;
